import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { AnimatePresence, motion } from "framer-motion";
import { jsPDF } from "jspdf";
import ForceGraph2D from "react-force-graph-2d";
import * as XLSX from "xlsx";
import { useFavorites } from "../hooks/useFavorites";

// ---------- helpers ----------
const normalizeTaxaName = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/[^a-z0-9\s/-]/gi, "")
    .replace(/\s+/g, " ")
    .trim();

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Title);

// ---------- main ----------
export default function StudyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [study, setStudy] = useState(() => {
    // Instantly populate from sessionStorage cache if available (user came from search/category)
    try {
      const raw = sessionStorage.getItem("sbdb_studies_v1");
      if (raw) {
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts < 5 * 60 * 1000) {
          return data.find((r) => String(r.id) === String(id)) || null;
        }
      }
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [chartType, setChartType] = useState("bar");
  const [selectedTaxa, setSelectedTaxa] = useState(null);
  const [bacteriaLinks, setBacteriaLinks] = useState({});
  const { isFavorite, toggleFavorite } = useFavorites();

  // load study — skip API call if cache already resolved it
  useEffect(() => {
    if (study) { setLoading(false); return; }
    fetch(`/api/studies/${id}`)
      .then((r) => {
        if (!r.ok) return null;
        return r.json();
      })
      .then((data) => setStudy(data || null))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // load taxa link dictionary in the background (non-blocking, browser-cached after first visit)
  useEffect(() => {
    fetch("/bacteria-links.json")
      .then((r) => r.json())
      .then((raw) => {
        const cleaned = {};
        Object.entries(raw).forEach(([k, v]) => (cleaned[normalizeTaxaName(k)] = v));
        setBacteriaLinks(cleaned);
      })
      .catch((e) => console.error("links load fail", e));
  }, []);

  const normalizeMethod = (val) =>
    (val || "").replace(/16S rRNA sequencing/gi, "16S rRNA gene sequencing");

  // ---------- group context: ALWAYS talk from Group B (experimental) ----------
  const getGroupContext = (row) => {
    const GA = (row?.Group_A || "Group A").trim(); // control
    const GB = (row?.Group_B || "Group B").trim(); // experimental
    return {
      GA,
      GB,
      incShort: `Higher in ${GB} (vs ${GA})`,
      decShort: `Lower in ${GB} (higher in ${GA})`,
    };
  };

  // ---------- chart data from Increased/Decreased ----------
  const processTaxaChartData = (row) => {
    if (!row) return [];
    const isValid = (s) =>
      typeof s === "string" &&
      s.trim().length > 0 &&
      !["na", "n/a", "/a", "nan", "none"].includes(s.trim().toLowerCase());
    const split = (s) =>
      String(s || "")
        .split(",")
        .map((x) => x.trim())
        .filter(isValid);

    const inc = split(row.Increased).map((name) => ({ name, value: 1 }));
    const dec = split(row.Decreased).map((name) => ({ name, value: -1 }));
    return [...inc, ...dec];
  };

  // ---------- pretty PDF (stable spacing + bullet lists + page breaks) ----------
  const downloadPDF = () => {
    if (!study) return;
    const { GA, GB } = getGroupContext(study);

    const doc = new jsPDF({ unit: "pt", format: "letter" }); // 612 x 792
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 48; // margin
    const ROW_H = 24; // consistent line height for table rows
    const LABEL_W = 160; // left column width
    const FONT = { normal: 11, small: 10, h1: 22, h2: 13 };
    let y = M;

    const newPage = () => {
      doc.addPage();
      y = M;
    };
    const need = (h) => {
      if (y + h > H - M) newPage();
    };

    // header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FONT.h1);
    doc.text("Study Overview", W / 2, y, { align: "center" });
    y += 16;
    doc.setDrawColor(0, 121, 192);
    doc.setLineWidth(2);
    doc.line(M, y, W - M, y);
    y += 18;

    // subtitle (article title)
    const title = study.Article_title || study.manuscript_title || "Untitled Study";
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    const titleLines = doc.splitTextToSize(title, W - 2 * M);
    need(12 * titleLines.length + 6);
    doc.text(titleLines, M, y);
    y += 12 * titleLines.length + 10;

    // prominent article link box
    if (study.DOI) {
      const doiUrl = /^https?:\/\//i.test(study.DOI)
        ? study.DOI
        : `https://doi.org/${study.DOI}`;
      const urlLines = doc.splitTextToSize(doiUrl, W - 2 * M - 18);
      const boxH = 14 + urlLines.length * 12 + 12;
      need(boxH);
      doc.setFillColor(232, 244, 252);
      doc.setDrawColor(0, 121, 192);
      doc.setLineWidth(0.75);
      doc.roundedRect(M, y - 14, W - 2 * M, boxH, 3, 3, "FD");
      // label
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0, 121, 192);
      doc.text("Access Original Article:", M + 8, y);
      y += 14;
      // URL (wrapped, clickable)
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      urlLines.forEach((ln, i) => {
        if (i === 0) {
          doc.textWithLink(ln, M + 8, y, { url: doiUrl });
        } else {
          doc.text(ln, M + 8, y);
        }
        y += 12;
      });
      doc.setTextColor(33, 33, 33);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(FONT.normal);
    }
    y += 10;

    // section label
    const section = (name) => {
      need(ROW_H * 1.6);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(FONT.h2);
      doc.setTextColor(0, 121, 192);
      doc.text(name, M, y);
      y += 10;
      doc.setDrawColor(220);
      doc.setLineWidth(1);
      doc.line(M, y, W - M, y);
      y += 14;
      doc.setTextColor(33, 33, 33);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(FONT.normal);
    };

    // single table row with wrapping on both label and value
    const row = (label, value, opts = {}) => {
      const v = value == null || String(value).trim() === "" ? "—" : String(value);
      const LH = 14; // line height for wrapped text
      const labelLines = doc.splitTextToSize(`${label}:`, LABEL_W - 4);
      const valueLines = doc.splitTextToSize(v, W - 2 * M - LABEL_W);
      const totalH = Math.max(ROW_H, LH * Math.max(labelLines.length, valueLines.length) + 8);
      need(totalH);

      doc.setFont("helvetica", "bold");
      labelLines.forEach((ln, i) => doc.text(ln, M, y + i * LH));
      doc.setFont("helvetica", "normal");

      if (opts.link && v !== "—") {
        const url = opts.link === "doi" && !/^https?:\/\//i.test(v) ? `https://doi.org/${v}` : v;
        doc.setTextColor(0, 121, 192);
        doc.textWithLink(valueLines[0], M + LABEL_W, y, { url });
        doc.setTextColor(33, 33, 33);
        for (let i = 1; i < valueLines.length; i++) doc.text(valueLines[i], M + LABEL_W, y + i * LH);
      } else {
        valueLines.forEach((ln, i) => doc.text(ln, M + LABEL_W, y + i * LH));
      }
      y += totalH;
    };

    // bullet list (for Increased/Decreased)
    // Label prints full-width so long perspective labels don't overflow the value column
    const bullets = (label, rawList) => {
      const items = String(rawList || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

      const LH = 14;
      const labelLines = doc.splitTextToSize(`${label}:`, W - 2 * M);
      need(LH * labelLines.length + 6);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(FONT.normal);
      labelLines.forEach((ln, i) => doc.text(ln, M, y + i * LH));
      y += LH * labelLines.length + 6;
      doc.setFont("helvetica", "normal");

      if (!items.length) { y += 4; return; }

      items.forEach((item) => {
        const lines = doc.splitTextToSize(item, W - 2 * M - 28);
        need(Math.max(ROW_H, LH * lines.length + 4));
        doc.circle(M + 10, y - 4, 1.5, "F");
        lines.forEach((ln, i) => doc.text(ln, M + 20, y + i * LH));
        y += Math.max(ROW_H, LH * lines.length + 4);
      });
      y += 6;
    };

    // Overview
    section("Overview");
    row("Study Type", study.Study_type);
    row("Sample Type", study.Sample_type);
    row("Host", study.Host);
    row("Epilepsy Type", study.Epilepsy_type);
    row("Authors", study.Authors);
    row("Journal", study.Journal);
    row("Year", (study.Year_of_publication || "").toString().replace(/,/g, ""));
    row("DOI", study.DOI, { link: "doi" });

    // Participants
    section("Participants");
    row("Age Category", study.Age_category);
    row("Age", study.Age);
    row("Country", study.Country);
    row("Group A (control)", GA);
    row("Group B (experimental)", GB);
    row("# Participants (A)", study["#_participants_in_group_A"]);
    row("# Participants (B)", study["#_participants_in_group_B"]);

    // Methods & Data
    section("Methods & Data");
    row("Method", normalizeMethod(study.Method));
    row("DNA Isolation Kit", study.DNA_isolation_kit);
    row("Sequencing Platform", study.Sequencing_platform);
    row("Sequencing Primers", study.Sequencing_primers);
    row("Sequencing Region", study.Sequencing_region);
    row("Data Accession ID", study.Data_accession_ID);
    row("Data Accession Link", study.Data_accession_link, { link: true });

    // Results & Metadata — put Comparison FIRST
    section("Results & Metadata");
    row("Comparison", study.Comparison);
    bullets(`Increased Taxa (Perspective: ${GB})`, study.Increased);
    bullets(`Decreased Taxa (Perspective: ${GB})`, study.Decreased);

    doc.save("study_overview.pdf");
  };

  const downloadExcel = () => {
    if (!study) return;
    const { GA, GB } = getGroupContext(study);
    const rows = [
      ["Field", "Value"],
      ["Article Title", study.Article_title || ""],
      ["Authors", study.Authors || ""],
      ["Journal", study.Journal || ""],
      ["Year", (study.Year_of_publication || "").toString().replace(/,/g, "")],
      ["DOI", study.DOI || ""],
      ["Study Type", study.Study_type || ""],
      ["Sample Type", study.Sample_type || ""],
      ["Host", study.Host || ""],
      ["Epilepsy Type", study.Epilepsy_type || ""],
      ["Age Category", study.Age_category || ""],
      ["Age", study.Age || ""],
      ["Country", study.Country || ""],
      ["Group A (Control)", GA],
      ["Group B (Experimental)", GB],
      ["# Participants Group A", study["#_participants_in_group_A"] || ""],
      ["# Participants Group B", study["#_participants_in_group_B"] || ""],
      ["Sample Size (Patients)", study.Sample_size_Patients || ""],
      ["Method", normalizeMethod(study.Method)],
      ["DNA Isolation Kit", study.DNA_isolation_kit || ""],
      ["Sequencing Platform", study.Sequencing_platform || ""],
      ["Sequencing Primers", study.Sequencing_primers || ""],
      ["Sequencing Region", study.Sequencing_region || ""],
      ["Data Accession ID", study.Data_accession_ID || ""],
      ["Data Accession Link", study.Data_accession_link || ""],
      ["Comparison", study.Comparison || ""],
      [`Increased Taxa (Higher in ${GB})`, study.Increased || ""],
      [`Decreased Taxa (Lower in ${GB})`, study.Decreased || ""],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 35 }, { wch: 80 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Study Data");
    const safeTitle = (study.Article_title || `study_${study.id}`)
      .replace(/[^a-z0-9]/gi, "_")
      .slice(0, 50);
    XLSX.writeFile(wb, `${safeTitle}.xlsx`);
  };

  // ---------- network (optional) ----------
  const TaxaNetwork = ({ taxaName, type, incLabel, decLabel }) => {
    const fgRef = useRef();
    const placeholderEffects = [
      "Inflammation",
      "Short-chain fatty acids",
      "Gut permeability",
      "Cognitive function",
      "Immune response",
    ];
    const color = type === "increase" ? "green" : "red";
    const centerX = 0;
    const centerY = 0;
    const radius = 100;

    const nodes = [
      { id: taxaName, group: 1, x: centerX, y: centerY, fx: centerX, fy: centerY },
      ...placeholderEffects.map((effect, i, arr) => {
        const angle = (i / arr.length) * 2 * Math.PI;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        return { id: effect, group: 2, x, y, fx: x, fy: y };
      }),
    ];

    const links = placeholderEffects.map((effect) => ({
      source: taxaName,
      target: effect,
      color,
      width: 2,
    }));

    useEffect(() => {
      if (fgRef.current) fgRef.current.zoomToFit(400, 40);
    }, [taxaName]);

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "1rem",
          backgroundColor: "#f9f9f9",
          borderRadius: "10px",
          padding: "2rem 1rem 2.5rem",
        }}
      >
        <ForceGraph2D
          ref={fgRef}
          graphData={{ nodes, links }}
          width={500}
          height={400}
          nodeAutoColorBy="group"
          linkColor={(l) => l.color}
          linkWidth={(l) => l.width}
          nodeLabel="id"
          enableNodeDrag={false}
          enableZoomPanInteraction={false}
          d3VelocityDecay={1}
          cooldownTicks={0}
          backgroundColor="#ffffff"
          nodeCanvasObject={(node, ctx, gs) => {
            const label = node.id;
            const fontSize = 14 / gs;
            ctx.font = `${fontSize}px Inter`;
            ctx.fillStyle = node.group === 1 ? "#0079C0" : "#555";
            ctx.beginPath();
            ctx.arc(node.x, node.y, 8, 0, 2 * Math.PI, false);
            ctx.fill();
            ctx.textAlign = "center";
            ctx.textBaseline = "top";
            ctx.fillText(label, node.x, node.y + 10);
          }}
        />
        <div style={{ display: "flex", gap: "2rem", marginTop: "2rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div style={{ width: 16, height: 16, backgroundColor: "green" }} />
            <span>{incLabel}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div style={{ width: 16, height: 16, backgroundColor: "red" }} />
            <span>{decLabel}</span>
          </div>
        </div>
      </div>
    );
  };

  // ---------- taxa chart ----------
  const TaxaChart = ({ data, chartType, incLabel, decLabel }) => {
    const COLORS = { increased: "#0079C0", decreased: "#FF9F1C" };

    const Legend = () => (
      <div style={{ display: "flex", justifyContent: "center", gap: "2rem", marginTop: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div style={{ width: 16, height: 16, backgroundColor: COLORS.increased }} />
          <span>{incLabel}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div style={{ width: 16, height: 16, backgroundColor: COLORS.decreased }} />
          <span>{decLabel}</span>
        </div>
      </div>
    );

    if (chartType === "donut") {
      const donutData = {
        labels: [incLabel, decLabel],
        datasets: [
          {
            data: [data.filter((d) => d.value > 0).length, data.filter((d) => d.value < 0).length],
            backgroundColor: [COLORS.increased, COLORS.decreased],
          },
        ],
      };
      return (
        <div style={{ height: 300 }}>
          <Doughnut data={donutData} options={{ maintainAspectRatio: false }} />
          <Legend />
        </div>
      );
    }

    return (
      <div>
        <Bar
          data={{
            labels: data.map((d) => d.name),
            datasets: [
              {
                label: "Taxa Change",
                data: data.map((d) => d.value),
                backgroundColor: data.map((d) =>
                  d.value > 0 ? COLORS.increased : COLORS.decreased
                ),
              },
            ],
          }}
          options={{
            indexAxis: "y",
            responsive: true,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => (ctx.raw > 0 ? incLabel : decLabel),
                },
              },
            },
            scales: {
              x: { display: false, grid: { display: false } },
              y: { grid: { display: false } },
            },
            onClick: (_e, elements, chart) => {
              if (!elements?.length) return;
              const { index } = elements[0];
              setSelectedTaxa({
                name: chart.data.labels[index],
                value: chart.data.datasets[0].data[index],
              });
            },
          }}
        />
        <Legend />
      </div>
    );
  };

  // ---------- render ----------
  if (loading) return <p style={{ padding: "2rem" }}>Loading…</p>;
  if (!study) return <p style={{ padding: "2rem", color: "red" }}>Study not found.</p>;

  const taxaData = processTaxaChartData(study);
  const { incShort, decShort, GB } = { ...getGroupContext(study), GB: getGroupContext(study).GB };

  // “Comparison” first in the last card
  const contentGroups = [
    [
      { label: "Study Type", value: study.Study_type },
      { label: "Sample Type", value: study.Sample_type },
      { label: "Host", value: study.Host },
      { label: "Epilepsy Type", value: study.Epilepsy_type },
      { label: "Authors", value: study.Authors },
      { label: "Journal", value: study.Journal },
      { label: "Year", value: study.Year_of_publication != null ? String(study.Year_of_publication).replace(/,/g, "") : null },
      { label: "DOI", value: study.DOI, isLink: true },
    ],
    [
      { label: "Age Category", value: study.Age_category },
      { label: "Age", value: study.Age },
      { label: "Country", value: study.Country },
      { label: "Group A", value: study.Group_A },
      { label: "Group B", value: study.Group_B },
      { label: "# Participants in Group A", value: study["#_participants_in_group_A"] },
      { label: "# Participants in Group B", value: study["#_participants_in_group_B"] },
    ],
    [
      { label: "Method", value: normalizeMethod(study.Method) },
      { label: "DNA Isolation Kit", value: study.DNA_isolation_kit },
      { label: "Sequencing Platform", value: study.Sequencing_platform },
      { label: "Sequencing Primers", value: study.Sequencing_primers },
      { label: "Sequencing Region", value: study.Sequencing_region },
      { label: "Data Accession ID", value: study.Data_accession_ID },
      { label: "Data Accession Link", value: study.Data_accession_link, isLink: true },
    ],
    [
      { label: "Comparison", value: study.Comparison },
      { label: `Increased (Higher in ${GB})`, value: study.Increased },
      { label: `Decreased (Lower in ${GB})`, value: study.Decreased },
    ],
  ];

  return (
    <div style={{ display: "flex" }}>
      {/* left rail */}
      <div
        style={{
          width: 180,
          padding: "1rem",
          background: "#f1f5f9",
          borderRight: "1px solid #e0e0e0",
          position: "fixed",
          top: "50%",
          transform: "translateY(-50%)",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "0.6rem 1rem",
            fontWeight: 500,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#475569",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: "0.85rem",
            textAlign: "left",
          }}
        >
          ← Back to results
        </button>
        <button
          onClick={() => setActiveTab("overview")}
          style={{
            padding: "0.75rem 1rem",
            fontWeight: 600,
            border: "1px solid #0079C0",
            background: activeTab === "overview" ? "#0079C0" : "transparent",
            color: activeTab === "overview" ? "#fff" : "#0079C0",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab("taxa")}
          style={{
            padding: "0.75rem 1rem",
            fontWeight: 600,
            border: "1px solid #0079C0",
            background: activeTab === "taxa" ? "#0079C0" : "transparent",
            color: activeTab === "taxa" ? "#fff" : "#0079C0",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Taxa Overview
        </button>
        <button
          onClick={downloadPDF}
          style={{
            padding: "0.75rem 1rem",
            fontWeight: 600,
            border: "1px solid #0079C0",
            background: "#fff",
            color: "#0079C0",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Download PDF
        </button>
        <button
          onClick={downloadExcel}
          style={{
            padding: "0.75rem 1rem",
            fontWeight: 600,
            border: "1px solid #10b981",
            background: "#fff",
            color: "#10b981",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Download Excel
        </button>
        <button
          onClick={() => study && toggleFavorite(study.Article_title)}
          style={{
            padding: "0.75rem 1rem",
            fontWeight: 600,
            border: study && isFavorite(study.Article_title)
              ? "1px solid #ef4444"
              : "1px solid #0079C0",
            background: study && isFavorite(study.Article_title)
              ? "rgba(239,68,68,0.08)"
              : "#fff",
            color: study && isFavorite(study.Article_title) ? "#ef4444" : "#0079C0",
            borderRadius: 6,
            cursor: "pointer",
            transition: "all 0.2s",
          }}
        >
          {study && isFavorite(study.Article_title) ? "♥ Saved" : "♡ Favourite"}
        </button>
      </div>

      {/* main */}
      <div
        style={{
          flex: 1,
          padding: "2rem 3rem 2rem 220px",
          maxWidth: 1100,
          margin: "0 auto",
          fontFamily: "Inter, Segoe UI, sans-serif",
        }}
      >
        <AnimatePresence mode="wait">
          {activeTab === "overview" && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h2
                style={{
                  color: "#0079C0",
                  marginBottom: "2rem",
                  fontSize: "2rem",
                  textAlign: "center",
                }}
              >
                {study.Article_title || study.manuscript_title || "Untitled Study"}
                <div
                  style={{
                    width: 80,
                    height: 4,
                    backgroundColor: "#0079C0",
                    margin: "0.5rem auto 2rem",
                    borderRadius: 2,
                  }}
                />
              </h2>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: "1.5rem",
                  padding: "0.5rem 0 2rem",
                }}
              >
                {["Study Overview", "Participant Info", "Methods & Data", "Results & Metadata"].map(
                  (title, i) => (
                    <div
                      key={i}
                      style={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 8,
                        padding: "1.5rem",
                        backgroundColor: "#fff",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      <h3 style={{ fontSize: "1.25rem", color: "#333", marginBottom: "1rem" }}>
                        {title}
                      </h3>
                      {contentGroups[i].map(({ label, value, isLink }, idx) => {
                        let display = value || "—";
                        let url = null;

                        if (isLink) {
                          if (value?.includes("<a")) {
                            const m = value.match(/href=\"([^\"]+)\"/);
                            url = m?.[1] || null;
                            display = url ? "View Link" : "No link provided";
                          } else if (label === "Data Accession Link" && value) {
                            url = value;
                          } else if (label === "DOI" && value) {
                            url = `https://doi.org/${value}`;
                          } else {
                            url = value;
                          }
                        }

                        // linkify taxa names for Increased/Decreased (perspective already baked in labels)
                        if ((label.startsWith("Increased") || label.startsWith("Decreased")) && value) {
                          const parts = String(value)
                            .split(",")
                            .map((t) => t.trim())
                            .filter(Boolean);
                          return (
                            <div key={idx} style={{ marginBottom: "0.75rem", color: "#555" }}>
                              <strong>{label}:</strong>{" "}
                              {parts.map((taxa, j) => {
                                const link = bacteriaLinks[normalizeTaxaName(taxa)];
                                return (
                                  <span key={j}>
                                    {link ? (
                                      <a
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#0079C0", textDecoration: "underline" }}
                                      >
                                        {taxa}
                                      </a>
                                    ) : (
                                      taxa
                                    )}
                                    {j < parts.length - 1 ? ", " : ""}
                                  </span>
                                );
                              })}
                            </div>
                          );
                        }

                        const isDOI = label === "DOI" && value;
                        return (
                          <p key={idx} style={{ marginBottom: "0.5rem", color: "#555", display: "flex", alignItems: "baseline", gap: "0.4rem", flexWrap: "wrap" }}>
                            <strong>{label}:</strong>{" "}
                            {isLink && url ? (
                              <a href={url} target="_blank" rel="noopener noreferrer" className="link-blue">
                                {display}
                              </a>
                            ) : (
                              display
                            )}
                            {isDOI && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(url || value);
                                  const btn = document.getElementById(`copy-doi-${idx}`);
                                  if (btn) { btn.textContent = "Copied!"; setTimeout(() => { btn.textContent = "Copy"; }, 1500); }
                                }}
                                id={`copy-doi-${idx}`}
                                style={{ fontSize: "0.7rem", padding: "0.1rem 0.4rem", border: "1px solid #cbd5e1", borderRadius: 4, background: "#f8fafc", color: "#64748b", cursor: "pointer", flexShrink: 0 }}
                              >
                                Copy
                              </button>
                            )}
                          </p>
                        );
                      })}
                    </div>
                  )
                )}
              </div>
            </motion.div>
          )}

          {activeTab === "taxa" && (
            <motion.div
              key="taxa"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              style={{ minHeight: 300 }}
            >
              {taxaData.length > 0 ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
                  <motion.div
                    style={{
                      backgroundColor: "#fff",
                      padding: "2rem",
                      borderRadius: 8,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      width: "100%",
                      maxWidth: 800,
                    }}
                  >
                    <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                      <h3 style={{ fontSize: "1.75rem", color: "#0079C0", marginBottom: "0.5rem" }}>
                        Taxa Overview
                      </h3>
                      <div
                        style={{
                          width: 60,
                          height: 4,
                          backgroundColor: "#0079C0",
                          margin: "0 auto",
                          borderRadius: 2,
                        }}
                      />
                      <p style={{ marginTop: "0.75rem", color: "#555" }}>
                        Overview of taxa changes (perspective: {study.Group_B || "Group B"})
                      </p>
                      <button
                        onClick={() => setChartType(chartType === "bar" ? "donut" : "bar")}
                        style={{
                          marginTop: "1rem",
                          backgroundColor: "#0079C0",
                          color: "#fff",
                          padding: "0.5rem 1rem",
                          borderRadius: 4,
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        Switch to {chartType === "bar" ? "Donut" : "Bar"} View
                      </button>
                    </div>

                    <TaxaChart data={taxaData} chartType={chartType} incLabel={incShort} decLabel={decShort} />

                    {selectedTaxa && (
                      <div style={{ marginTop: "2rem" }}>
                        <h4 style={{ color: "#0079C0" }}>
                          Network for: <em>{selectedTaxa.name}</em>
                        </h4>
                        <TaxaNetwork
                          taxaName={selectedTaxa.name}
                          type={selectedTaxa.value > 0 ? "increase" : "decrease"}
                          incLabel={incShort}
                          decLabel={decShort}
                        />
                      </div>
                    )}
                  </motion.div>
                </div>
              ) : (
                <p style={{ textAlign: "center", padding: "2rem", color: "#777" }}>
                  No taxa data available for this study.
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
